import { ChevronLeft } from "lucide-react";

import { SVG } from "@components/items/common/SVG";
import LibraryProgressCard from "@components/items/display/LibraryProgressCard";
import TranslatedText from "@components/items/display/TranslatedText";
import { LibraryItemWithProgressOutput } from "@lib/stores/myLibrary/types";
import { Badge } from "@ui/badge";
import { ShadcnButton } from "@components/items/display/ShadcnButton";
import { LibraryItemDetails } from "./LibraryItemDetailsModal";
import { GenericLoading } from "@components/items/common/GenericLoading";
import { HorizontalListSkeleton } from "./HorizontalListSkeleton";

interface LibraryProgressListProps {
  contents: LibraryItemWithProgressOutput[];
  badge: {
    title: string;
    icon: string;
    style: string;
  };
  noContent: {
    icon: string;
    description: string;
    title?: string;
    buttonText?: string;
    onButtonClick?: () => void;
  };
  countTitle: string | JSX.Element;
  onItemPress?:
    | ((item: LibraryItemDetails) => void | Promise<void>)
    | undefined;
  isLoading?: boolean;
  onSeeMoreClick?: () => void;
  hidePremiumIcon?: boolean;
}

const MAX = 5;

export const LibraryProgressList = ({
  contents,
  badge,
  noContent,
  countTitle,
  onItemPress,
  isLoading,
  onSeeMoreClick,
  hidePremiumIcon,
}: LibraryProgressListProps) => {
  const visibleItems = contents.slice(0, MAX);

  if (isLoading && !contents.length) {
    return (
      <div className="pb-5">
        <HorizontalListSkeleton />
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col items-start justify-center gap-3 p-5 pb-10 text-black dark:text-white">
      <Badge className={`py-1 text-black ${badge.style}`}>
        <div className="flex items-center justify-center gap-2">
          <div>
            <SVG src={badge.icon} className="h-5 w-5" />
          </div>
          <div>{badge.title}</div>
        </div>
      </Badge>
      {contents.length ? (
        <>
          <div className="flex w-full items-center justify-between px-3">
            <div className="flex items-center justify-start gap-2">
              {countTitle}
            </div>
            {contents.length > MAX ? (
              <div
                className="flex cursor-pointer items-center justify-center gap-1"
                onClick={onSeeMoreClick}
              >
                <TranslatedText
                  text={`my-library.${"show all"}`}
                  className="flex cursor-pointer items-center justify-center hover:opacity-75"
                />
                <ChevronLeft strokeWidth={1.5} className="ltr:rotate-180" />
              </div>
            ) : null}
          </div>
          <div className="flex w-full items-start justify-start gap-4">
            {visibleItems.map(
              ({
                id,
                contentEntityTypeId,
                title,
                fullImageUrl,
                authors,
                progress,
                count,
                contentEntityType,
                isLocked,
              }) => {
                const progressModified = Number(
                  ((progress || 0) * 100)?.toFixed(0)
                );

                return (
                  <LibraryProgressCard
                    key={id}
                    image={fullImageUrl || ""}
                    title={title || ""}
                    author={authors?.join(", ") || ""}
                    progress={progressModified}
                    onClick={() => {
                      onItemPress &&
                        onItemPress({
                          id,
                          contentEntityTypeId,
                          title,
                          author: authors?.join(", "),
                          image: fullImageUrl,
                          count,
                          progress: progressModified,
                          contentEntityType,
                          isLocked,
                        });
                    }}
                    count={count}
                    countColorStyle={badge.style}
                    isLocked={isLocked}
                    hidePremiumIcon={hidePremiumIcon}
                  />
                );
              }
            )}
          </div>
        </>
      ) : !isLoading ? (
        <div className="flex w-full flex-col items-center justify-center gap-2 py-6">
          <SVG src={noContent.icon} />
          <div className="text-[20px] font-medium text-black dark:text-white">
            {noContent.title}
          </div>
          <div className="flex flex-col items-center justify-center gap-4">
            {noContent.description}
            {noContent.buttonText ? (
              <ShadcnButton
                className="rounded-xl bg-primary"
                onClick={noContent.onButtonClick}
              >
                {noContent.buttonText}
              </ShadcnButton>
            ) : null}
          </div>
        </div>
      ) : (
        // TODO
        <GenericLoading />
      )}
    </div>
  );
};

export default LibraryProgressList;
